import { Link } from 'react-router-dom';
import { Heart, Phone, Mail, MapPin, Clock, Facebook, Instagram } from 'lucide-react';

export const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-[#2a2873] via-[#1a1853] to-[#2a2873] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_quickcert-ga/artifacts/ccwipicm_LifeBreath%20CPR%20%26%20First%20Aid%20logo%20%282%29.png" 
                alt="LifeBreath CPR & First Aid" 
                className="h-16 w-auto bg-white rounded-lg p-2"
              />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed">
              Empowering communities to respond with confidence, compassion, and care when every breath matters.
            </p>
            <a href="mailto:care@lifebreathcpr.com" className="text-[#e05b6d] hover:text-white text-sm transition-colors font-medium">
              care@lifebreathcpr.com
            </a>
            <div className="flex items-center gap-3">
              <a 
                href="https://facebook.com/LifeBreathCPR" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-gradient-to-br from-[#c31c31] to-[#e05b6d] flex items-center justify-center hover:scale-110 transition-transform"
                aria-label="Follow us on Facebook"
                data-testid="facebook-link"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href="https://instagram.com/LifeBreathCPR" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5a2b8a] to-[#8c53b0] flex items-center justify-center hover:scale-110 transition-transform"
                aria-label="Follow us on Instagram"
                data-testid="instagram-link"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/cpr-training" className="text-white/70 hover:text-white transition-colors text-sm">
                  CPR & First Aid Training
                </Link>
              </li>
              <li>
                <Link to="/blended-courses" className="text-white/70 hover:text-white transition-colors text-sm">
                  Blended Courses
                </Link>
              </li>
              <li>
                <Link to="/group-classes" className="text-white/70 hover:text-white transition-colors text-sm">
                  Group Classes
                </Link>
              </li>
              <li>
                <Link to="/mobile-notary" className="text-white/70 hover:text-white transition-colors text-sm">
                  Mobile Notary (GA)
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/book-now" className="text-white/70 hover:text-white transition-colors text-sm">
                  Book Now
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-white/70 hover:text-white transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/faqs" className="text-white/70 hover:text-white transition-colors text-sm">
                  FAQs
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white/70 hover:text-white transition-colors text-sm">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#e05b6d] flex-shrink-0 mt-0.5" />
                <div>
                  <a href="tel:912-388-6305" className="text-white hover:text-[#e05b6d] transition-colors font-medium">
                    912-388-6305
                  </a>
                  <span className="text-white/60 text-sm block">Georgia (Hope N.)</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#4f62c8] flex-shrink-0 mt-0.5" />
                <div>
                  <a href="tel:904-465-5065" className="text-white hover:text-[#4f62c8] transition-colors font-medium">
                    904-465-5065
                  </a>
                  <span className="text-white/60 text-sm block">Jacksonville, FL (Shiffon)</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#8c53b0] flex-shrink-0 mt-0.5" />
                <div>
                  <a href="mailto:care@lifebreathcpr.com" className="text-white hover:text-[#8c53b0] transition-colors font-medium">
                    care@lifebreathcpr.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#e05b6d] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white/70 text-sm">Mon-Fri: 9AM-6PM</span>
                  <span className="text-white/60 text-sm block">Sat: 10AM-4PM | Sun: By Appt</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} LifeBreath CPR & First Aid. All rights reserved.
          </p>
          <p className="text-white/50 text-sm">
            Because Every Breath Matters.
          </p>
        </div>
      </div>
    </footer>
  );
};
